import { useCallback, useEffect, useRef, useState } from "react";
import { INSPECTOR_CHROME_FLASH_EVENT } from "../utils/inspectorChromeFlash";

/**
 * Listens for {@link INSPECTOR_CHROME_FLASH_EVENT} and drives the inspector root class for the CSS pulse.
 *
 * @param {{ minimizedRef: import("react").RefObject<boolean>, setMinimized: (v: boolean) => void }} params
 */
export function useInspectorChromeFlash({ minimizedRef, setMinimized }) {
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);
  const timersRef = useRef({ arm: null, clear: null });

  const armFlash = useCallback(() => {
    const timers = timersRef.current;
    window.clearTimeout(timers.arm);
    window.clearTimeout(timers.clear);
    timers.arm = null;
    timers.clear = null;

    const wasMinimized = minimizedRef.current;
    setMinimized(false);
    setActive(false);

    const delayMs = wasMinimized ? 320 : 0;
    const animHoldMs = 820;

    timers.arm = window.setTimeout(() => {
      timers.arm = null;
      const activate = () => {
        activeRef.current = true;
        setActive(true);
        timers.clear = window.setTimeout(() => {
          activeRef.current = false;
          setActive(false);
          timers.clear = null;
        }, animHoldMs);
      };

      if (activeRef.current) {
        activeRef.current = false;
        setActive(false);
        requestAnimationFrame(() => {
          setActive(true);
          activeRef.current = true;
          timers.clear = window.setTimeout(() => {
            activeRef.current = false;
            setActive(false);
            timers.clear = null;
          }, animHoldMs);
        });
        return;
      }

      activate();
    }, delayMs);
  }, [minimizedRef, setMinimized]);

  useEffect(() => {
    const onFlash = () => armFlash();
    const timers = timersRef.current;
    window.addEventListener(INSPECTOR_CHROME_FLASH_EVENT, onFlash);
    return () => {
      window.removeEventListener(INSPECTOR_CHROME_FLASH_EVENT, onFlash);
      window.clearTimeout(timers.arm);
      window.clearTimeout(timers.clear);
    };
  }, [armFlash]);

  return {
    inspectorChromeFlashActive: active,
    inspectorChromeFlashRootClass: active ? "inspector-chrome-flash-active" : "",
  };
}
